/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FlatSubtenant, Tenant } from '@/client';
import { useUserSubtenantRoleQuery } from '@/hooks/query/useUserSubtenantRoleQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

/**
 * Custom hook that manages tenant and subtenant selection logic.
 * Consolidates all the complex state management from MainLayout.
 */
export function useTenantSubtenantManager() {
  const navigate = useNavigate();
  const { tenantID } = useParams();
  const { pathname } = useLocation();

  // State store selectors
  const currentUser = useMgmtPlaneStateStore((state) => state.userProfile);
  const stateSelectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantID = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);
  const selectedSubtenantParentID = useMgmtPlaneStateStore((state) => state.selectedSubtenantParentID);

  // URL tenant takes precedence over state (for superadmin navigation to different tenants)
  const selectedTenantID = tenantID || stateSelectedTenantID;

  // State store actions - using atomic setters to prevent race conditions
  const setSelectedTenant = useMgmtPlaneStateStore((state) => state.setSelectedTenant);
  const setSelectedSubtenant = useMgmtPlaneStateStore((state) => state.setSelectedSubtenant);
  const setSubtenantRole = useMgmtPlaneStateStore((state) => state.setSubtenantRole);

  // Initialize user profile-based state
  useEffect(() => {
    if (!currentUser) return;

    // Non-superadmins always use their own tenant (TenantSwitcher doesn't render for them)
    // Superadmins: only set on initial load when no URL tenant and no existing state
    // (URL-based navigation for superadmins is handled by TenantSwitcher's useEffect)
    if (currentUser.role !== 'superadmin' || (!tenantID && !stateSelectedTenantID)) {
      setSelectedTenant({ id: currentUser.tenantID, name: currentUser.tenantName });
    }

    // Handle subtenant-only users default subtenant
    if (currentUser.subtenantOnly && currentUser.defaultSubtenant && !selectedSubtenantID) {
      setSelectedSubtenant({
        id: currentUser.defaultSubtenant.id,
        name: currentUser.defaultSubtenant.name,
        parentId: currentUser.tenantID,
      });
    }
  }, [currentUser, tenantID, stateSelectedTenantID, selectedSubtenantID, setSelectedTenant, setSelectedSubtenant]);

  // Clear subtenant selection when tenant changes
  useEffect(() => {
    if (selectedTenantID) {
      const previousTenantID = localStorage.getItem('selectedTenantID');
      if (previousTenantID && previousTenantID !== selectedTenantID) {
        setSelectedSubtenant(undefined);
      }
    }
  }, [selectedTenantID, setSelectedSubtenant]);

  // Tenant selection handler
  const onSelectTenant = (tenant: Tenant) => {
    if (currentUser?.role !== 'superadmin') return;

    // Set tenant atomically (ID + name together)
    setSelectedTenant({ id: tenant.id, name: tenant.name });
    // Clear subtenant selection
    setSelectedSubtenant(undefined);

    // Handle route navigation for tenant switching
    if (pathname.includes('/tenants/')) {
      const newPath = pathname.replace(/\/tenants\/[a-fA-F0-9-]+/, `/tenants/${tenant.id}`);
      navigate(newPath);
    }
  };

  // Fetch user role for selected subtenant
  const { role: subtenantRole } = useUserSubtenantRoleQuery({
    subtenantId: selectedSubtenantID,
    parentId: selectedSubtenantParentID,
    enabled: !!selectedSubtenantID && !!selectedSubtenantParentID && !!currentUser?.id,
  });

  // Update subtenant role when it's fetched
  useEffect(() => {
    if (selectedSubtenantID && subtenantRole) {
      setSubtenantRole(subtenantRole);
    } else if (!selectedSubtenantID) {
      setSubtenantRole(undefined);
    }
  }, [selectedSubtenantID, subtenantRole, setSubtenantRole]);

  // Subtenant selection handler
  const onSelectSubtenant = (subtenant?: FlatSubtenant) => {
    if (subtenant) {
      setSelectedSubtenant({
        id: subtenant.id,
        name: subtenant.name,
        parentId: subtenant.parent?.id || '',
      });
    } else {
      // No subtenant selected, clear all related state
      setSelectedSubtenant(undefined);
    }
    // Role will be fetched automatically by the query hook when parentId is available
  };

  return {
    currentUser,
    selectedTenantID,
    selectedSubtenantID,
    onSelectTenant,
    onSelectSubtenant,
  };
}
